import { Input, Button } from '@bpinternal/ui-kit-next'
import { useNavigate, useSearch } from '@tanstack/react-router'
import { useCallback, useEffect, useRef, useState } from 'react'
import { cn } from '~/utils'
import { DropdownMenu, MenuItem } from '~/elements'
import { FILTER_SEPARATOR, parseParams, parseSearchString } from './parseParams'
import { ChevronDown, Search } from 'lucide-react'

export type FilterMenuInput = {
  name: string
  type?: 'string' | 'object' | 'array'
  value?: string | FilterMenuInput[]
  paramName?: string
  icon?: React.ReactNode
}

type Props = { className?: string; minimal?: boolean; filters: FilterMenuInput[] }

export const QueryParamBuilder = ({ className, minimal = false, filters, ...props }: Props) => {
  const searchInputRef = useRef<HTMLInputElement>(null)
  const queryParams = useSearch({ strict: false })
  const [filterInput, setFilterInput] = useState(parseParams(queryParams))

  const navigate = useNavigate()
  const updateSearchParams = (searchParams: Record<string, string | string[] | Record<string, string>>) => {
    void navigate({
      to: '.',
      params: (current: Record<string, unknown>) => current,
      search: () => searchParams,
    })
  }

  const addFilter = useCallback(
    ({ filter, value }: { filter: string; value?: string }) => {
      setFilterInput((prevInput) => `${prevInput.trim()} ${filter}${FILTER_SEPARATOR}${value ?? ''}`.trim())
    },
    [setFilterInput]
  )

  useEffect(() => {
    setFilterInput(parseParams(queryParams))
  }, [queryParams])

  function buildMenuItem(filter: FilterMenuInput): MenuItem {
    const { name, value, paramName, icon } = filter
    if (Array.isArray(value)) {
      return {
        type: 'submenu',
        content: name,
        items: value.map((f) => buildMenuItem(f)),
        leadingIcon: icon,
      }
    }
    return {
      type: 'item',
      content: name,
      leadingIcon: icon,
      onSelect: () => {
        addFilter({ filter: paramName ?? '', value })
      },
    }
  }

  const searchFilterMenu: MenuItem[] = [{ type: 'label', label: 'Filter by' }, ...filters.map(buildMenuItem)]

  return (
    <div {...props} className={cn('flex gap-2', className)}>
      <DropdownMenu
        content={searchFilterMenu}
        size={'2'}
        onCloseAutoFocus={(e) => {
          e.preventDefault()
          searchInputRef.current?.focus()
          searchInputRef.current?.setSelectionRange(filterInput.length, filterInput.length)
        }}
      >
        <Button variant="outline" className="text-neutral-900" onClick={() => {}}>
          Filters <ChevronDown className="size-4" />
        </Button>
      </DropdownMenu>
      <div className="relative grow">
        <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          ref={searchInputRef}
          value={filterInput}
          onChange={(e) => {
            setFilterInput(e.target.value)
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              const { parsedParams } = parseSearchString(filterInput, filters)
              updateSearchParams(parsedParams)
            }
          }}
          className="pl-10 text-neutral-800"
        />
      </div>
    </div>
  )
}
